(() => {
  'use strict';
  function create(storage, owner, labels, options = {}) {
    const key = `swanWishlistsV1:${owner}`, marker = `swanWishlistAccountImportedV1:${owner}`, defaultId = 'favourites';
    const endpoint = options.endpoint || '/apps/swan-design-studio/wishlists', fetcher = options.fetcher || window.fetch.bind(window);
    const blank = () => ({version: 2, revision: 0, lists: [{id: defaultId, name: labels.default_list, createdAt: 0}], items: []});
    const itemKey = item => `${item.type}:${item.id}`;
    let state = owner ? null : blank(), csrf = '', pending, busy = false, lastRefresh = 0, importError = '';
    const notify = () => options.onChange?.();
    const error = (message, code) => Object.assign(new Error(message), {code});
    async function request(path, body) {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetcher(endpoint + path, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: body ? {'Content-Type':'application/json'} : {'Accept':'application/json'}, body: body ? JSON.stringify({...body, _csrf:csrf}) : undefined});
        let data; try {data = await response.json();} catch (_) {throw error(labels.sync_error, 'NETWORK');}
        if (!response.ok) throw error(data.message || labels.sync_error, data.code || 'NETWORK');
        if (!data.state || data.state.version !== 2 || !Number.isSafeInteger(data.state.revision) || !Array.isArray(data.state.items) || !Array.isArray(data.state.lists)) throw error(labels.sync_error, 'NETWORK');
        if (data.customerId && data.customerId !== owner) throw error(labels.account_changed, 'ACCOUNT_CHANGED');
        return data;
      } catch (failure) { if (failure.code) throw failure; throw error(labels.sync_error, 'NETWORK'); }
      finally {clearTimeout(timeout);}
    }
    function browserSaves() {
      const raw = storage.getItem(key);
      if (raw) {
        const source = JSON.parse(raw);
        if (source.version !== 1 || !Array.isArray(source.lists) || !Array.isArray(source.items)) throw error(labels.import_error, 'IMPORT');
        return source;
      }
      const legacy = JSON.parse(storage.getItem(`swanShopByRoomFavorites:${owner}`) || '[]');
      if (!Array.isArray(legacy)) throw error(labels.import_error, 'IMPORT');
      return {version:1, lists:[{id:defaultId,name:labels.default_list,createdAt:0}],items:[...new Set(legacy.map(String))].filter(Boolean).map((id,index)=>({type:'gallery',id,title:`${labels.gallery} ${id}`,listIds:[defaultId],addedAt:0,order:index}))};
    }
    async function importBrowser() {
      importError = '';
      let source, fingerprint;
      try {
        source = browserSaves();
        if (!source.items.length && source.lists.length <= 1) return;
        fingerprint = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(source))))).map(n=>n.toString(16).padStart(2,'0')).join('');
        if (storage.getItem(marker) === fingerprint) return;
        const data = await request('/import', {source}); state = data.state;
        try {storage.setItem(marker, fingerprint);} catch (_) {}
      } catch (_) {importError = labels.import_error;}
    }
    function refresh({force = false} = {}) {
      if (!owner) return Promise.resolve(state);
      if (pending) return pending;
      if (busy || (!force && state && Date.now() - lastRefresh < 10000)) return Promise.resolve(state);
      pending = (async()=>{
        const data = await request(''); state = data.state; csrf = data.csrf;
        await importBrowser(); lastRefresh = Date.now(); notify(); return read();
      })().finally(()=>{pending=null;});
      return pending;
    }
    function read() {if (!state) throw error(labels.loading, 'LOADING');return structuredClone(state);}
    async function mutate(action, data, revision) {
      if (!owner) throw error(labels.signin_heading, 'SIGN_IN');
      if (!state) await refresh();
      if (busy) throw error(labels.saving, 'BUSY');
      busy = true;
      try {
        const result = await request('', {action,...data,revision:revision ?? state.revision,requestKey:crypto.randomUUID()});
        state = result.state; lastRefresh = Date.now(); notify(); return result;
      } catch (failure) {
        if (['WISHLIST_CONFLICT','SESSION_EXPIRED','NETWORK'].includes(failure.code)) {
          try {const current=await request('');state=current.state;csrf=current.csrf;notify();} catch (_) {}
        }
        throw failure;
      } finally {busy=false;}
    }
    return {key,defaultId,itemKey,read,refresh,get importError(){return importError;},
      createList:async(name,revision)=>(await mutate('create',{name},revision)).listId,
      renameList:(listId,name,revision)=>mutate('rename',{listId,name},revision),
      deleteList:(listId,revision)=>mutate('delete',{listId},revision),
      setItemLists:(item,listIds,newName='',revision)=>mutate('setItem',{item,listIds,newName},revision),
      removeItem:(item,revision)=>mutate('setItem',{item,listIds:[]},revision),
      has:(type,id)=>read().items.some(item=>item.type===type&&item.id===String(id))};
  }
  window.SwanWishlistStore = {create};
})();
